/**
 * ============================================================================
 * CourtsStats - KPI CARDS FOR COURTS METRICS
 * ============================================================================
 *
 * Displays 4 key metrics in card format matching PayoutsStats pattern
 *
 * ============================================================================
 */

import React from 'react';
import { Grid, CheckCircle, XCircle, DollarSign } from 'lucide-react';

interface CourtsStatsProps {
  totalCourts: number;
  activeCourts: number;
  inactiveCourts: number;
  courtsWithCustomPricing: number;
}

/**
 * Courts KPI stats cards
 */
export function CourtsStats({
  totalCourts,
  activeCourts,
  inactiveCourts,
  courtsWithCustomPricing,
}: CourtsStatsProps) {
  const stats = [
    {
      label: 'Total Courts',
      value: totalCourts.toString(),
      subtext: 'All courts/fields',
      icon: Grid,
      color: 'bg-blue-50 text-blue-600',
    },
    {
      label: 'Active',
      value: activeCourts.toString(),
      subtext: 'Available for booking',
      icon: CheckCircle,
      color: 'bg-green-50 text-green-600',
    },
    {
      label: 'Inactive',
      value: inactiveCourts.toString(),
      subtext: 'Not visible to customers',
      icon: XCircle,
      color: 'bg-red-50 text-red-600',
    },
    {
      label: 'Custom Pricing',
      value: courtsWithCustomPricing.toString(),
      subtext: 'Court-specific rates',
      icon: DollarSign,
      color: 'bg-purple-50 text-purple-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {stats.map((stat, idx) => (
        <div key={idx} className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">{stat.label}</p>
              <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stat.value}</p>
              <p className="text-xs text-gray-500 truncate">{stat.subtext}</p>
            </div>
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${stat.color} flex items-center justify-center flex-shrink-0 ml-2`}
            >
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
